if (!customElements.get("main-product-v3")) {
  class mainProductV3 extends HTMLElement {
    constructor() {
      super();
      this.product = null;
      this.selectedVariant = null;

      this.currency = window.Shopify?.currency?.active || 'USD';
      this.language = window.Shopify?.locale || 'en-US';
      this.moneyFormatter = new Intl.NumberFormat(this.language, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      this._boundOnKeyDown = null;
      this.stickyInstance = null;
      this._boundOnResize = null;
      this._galleryAnchor = null;
      this._isGalleryInMobilePosition = null;
      this._stickyIo = null;
      this._boundStickyResize = null;
      this.stickyPriceEl = null;
    }

    connectedCallback() {
      this.product = JSON.parse(this.querySelector(".main-product-json").innerHTML);

      // Swiper gallery (v3 simplified)
      this.mainSlider = this.querySelector('.main-product-v2__gallery');

      // Variant/ATC logic (reused v2 snippets/classes)
      this.optionInputs = this.querySelectorAll('.main-product-v2__input');
      this.sellingPlans = this.querySelectorAll('.main-product-v2__selling-plans');
      this.addToCartVarinatInput = this.querySelector('.main-product-v2__product-variant-id');
      this.addToCartButtons = this.querySelectorAll('.main-product-v2__atc-button');
      this.sellingPlanInputs = this.querySelectorAll('.main-product-v2__selling-plan-input');
      this.addToCartForm = this.querySelector('form[action="/cart/add"]');

      // PDP price element (new)
      this.priceValueEl = this.querySelector('.main-product-v3__price-value');

      this.initPopupBlocks();
      this.stylePopupButtonsFirstWord();
      this.styleTitleLastTwoWords();

      this.initSliders();
      this.initSelectedVariantAndUI();
      this.initMobileStickyAtc();
      this.initStickyLibrary();
      this.initResponsiveGalleryPosition();

      this.onChangeVariant();
      this.onChangeSellingPlans();
      this.handleAddToCartButtonClick();
      this.addToCartAndCheckout(this.addToCartForm);
      this.updateAddToCartAvailability();
    }

    initSelectedVariantAndUI() {
      this.selectedVariant = this.initSelectedVariant();

      if (this.selectedVariant) {
        this.updateContainerVariantAttribute(this.selectedVariant.id);
        this.initSellingPlanForVariant(this.selectedVariant.id);
      } else {
        // If no variant selected, show all images
        this.updateImageVisibility(null);
      }
    }

    initPopupBlocks() {
      const opens = this.querySelectorAll('[data-js-popup-open]');
      const overlays = this.querySelectorAll('[data-js-popup-overlay]');
      const closeBtns = this.querySelectorAll('[data-js-popup-close]');

      const closeOverlay = (overlay) => {
        if (!overlay) return;
        overlay.hidden = true;
        overlay.classList.remove('is-open');

        // If nothing else is open, restore scrolling.
        const anyOpen = Array.from(this.querySelectorAll('[data-js-popup-overlay]')).some((o) => !o.hidden);
        if (!anyOpen) document.body.style.overflow = '';
      };

      const openOverlay = (overlay) => {
        if (!overlay) return;

        // Close others first
        overlays.forEach((o) => {
          if (o !== overlay) closeOverlay(o);
        });

        overlay.hidden = false;
        overlay.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      };

      opens.forEach((btn) => {
        btn.addEventListener('click', () => {
          const wrapper = btn.closest('.main-product-v3__popup-block, .main-product-v3__guarantee-block');
          const overlay = wrapper ? wrapper.querySelector('[data-js-popup-overlay]') : null;
          openOverlay(overlay);
        });
      });

      overlays.forEach((overlay) => {
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) closeOverlay(overlay);
        });
      });

      closeBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          const overlay = btn.closest('[data-js-popup-overlay]');
          closeOverlay(overlay);
        });
      });

      // Escape support
      this._boundOnKeyDown = (e) => {
        if (e.key !== 'Escape') return;
        const openOverlayEl = Array.from(this.querySelectorAll('[data-js-popup-overlay]')).find((o) => !o.hidden);
        if (openOverlayEl) closeOverlay(openOverlayEl);
      };
      document.addEventListener('keydown', this._boundOnKeyDown);
    }

    disconnectedCallback() {
      if (this._boundOnKeyDown) document.removeEventListener('keydown', this._boundOnKeyDown);
      if (this.stickyInstance && typeof this.stickyInstance.destroy === 'function') {
        this.stickyInstance.destroy();
        this.stickyInstance = null;
      }
      if (this._boundOnResize) window.removeEventListener('resize', this._boundOnResize);
      if (this._stickyIo) {
        this._stickyIo.disconnect();
        this._stickyIo = null;
      }
      if (this._boundStickyResize) {
        window.removeEventListener('resize', this._boundStickyResize);
        this._boundStickyResize = null;
      }
    }

    initMobileStickyAtc() {
      const purchase = this.querySelector('.main-product-v3__purchase');
      const sticky = this.querySelector('.main-product-v3__sticky-atc');
      if (!purchase || !sticky) return;

      const targetAtc = purchase.querySelector(
        '.main-product-v2__atc-button.m-add2cart-v1',
      ) || purchase.querySelector('.main-product-v2__atc-button');
      if (!targetAtc) {
        sticky.remove();
        return;
      }

      this.stickyPriceEl = sticky.querySelector('.main-product-v3__sticky-atc-price');
      const stickyBtn = sticky.querySelector('.main-product-v3__sticky-atc-btn');
      if (stickyBtn) {
        stickyBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const atc =
            this.querySelector('.main-product-v3__purchase .main-product-v2__atc-button.m-add2cart-v1') ||
            this.querySelector('.main-product-v3__purchase .main-product-v2__atc-button');
          if (!atc || atc.disabled) return;
          atc.click();
        });
      }

      const setStickyActive = (active) => {
        if (window.innerWidth > 768) {
          sticky.classList.remove('is-active');
          sticky.setAttribute('aria-hidden', 'true');
          return;
        }
        sticky.classList.toggle('is-active', active);
        sticky.setAttribute('aria-hidden', active ? 'false' : 'true');
      };

      this._stickyIo = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          const purchaseVisible = entry.isIntersecting;
          setStickyActive(!purchaseVisible);
        },
        { threshold: 0, rootMargin: '0px' },
      );
      this._stickyIo.observe(purchase);

      this._boundStickyResize = () => {
        if (window.innerWidth > 768) setStickyActive(false);
      };
      window.addEventListener('resize', this._boundStickyResize);

      if (this.stickyPriceEl && this.selectedVariant) {
        const planId = this.querySelector('.main-product-v2__selling-plan-id')?.value || '';
        this.updateATCButtonPrice(planId);
      }
    }

    initStickyLibrary() {
      if (typeof window.Sticky !== 'function') return;
      this.stickyInstance = new window.Sticky('.main-product-v3__gallery-sticky');
    }

    stylePopupButtonsFirstWord() {
      const buttons = this.querySelectorAll('.main-product-v3__popup-open.main-product-v2__open-modal-btn');
      buttons.forEach((button) => {
        if (button.dataset.firstWordStyled === 'true') return;

        const text = (button.textContent || '').trim();
        if (!text) return;

        const parts = text.split(/\s+/);
        const firstWord = parts.shift();
        const rest = parts.join(' ');

        button.innerHTML = rest
          ? `<em>${firstWord}</em> ${rest}`
          : `<em>${firstWord}</em>`;
        button.dataset.firstWordStyled = 'true';
      });
    }

    styleTitleLastTwoWords() {
      const partSelector =
        '.main-product-v3__title-desktop, .main-product-v3__title-mobile, .main-product-v3__sticky-atc-title';
      let targets = [...this.querySelectorAll(partSelector)];

      if (!targets.length) {
        this.querySelectorAll('.main-product-v3__title').forEach((h1) => {
          if (!h1.querySelector('.main-product-v3__title-desktop')) targets.push(h1);
        });
      }

      targets.forEach((el) => {
        if (el.dataset.lastTwoWordsStyled === 'true') return;

        const text = (el.textContent || '').trim();
        if (!text) return;

        const words = text.split(/\s+/);
        if (words.length < 2) return;

        const lastTwoWords = words.slice(-2).join(' ');
        const firstPart = words.slice(0, -2).join(' ');

        el.innerHTML = firstPart
          ? `${firstPart} <em>${lastTwoWords}</em>`
          : `<em>${lastTwoWords}</em>`;

        el.dataset.lastTwoWordsStyled = 'true';
      });
    }

    initResponsiveGalleryPosition() {
      this.galleryCol = this.querySelector('.main-product-v3__gallery-col');
      this.layout = this.querySelector('.main-product-v3__layout');

      if (!this.galleryCol || !this.layout) return;

      // Keep original gallery mount point for restoring on desktop.
      this._galleryAnchor = document.createComment('main-product-v3-gallery-anchor');
      this.layout.insertBefore(this._galleryAnchor, this.galleryCol);

      this._boundOnResize = () => this.updateResponsiveGalleryPosition();
      window.addEventListener('resize', this._boundOnResize);

      this.updateResponsiveGalleryPosition();
    }

    updateResponsiveGalleryPosition() {
      if (!this.galleryCol) return;

      const isMobile = window.innerWidth <= 768;
      if (this._isGalleryInMobilePosition === isMobile) return;

      if (isMobile) {
        const description = this.querySelector('.main-product-v3__description');
        if (description && description.parentNode) {
          description.insertAdjacentElement('afterend', this.galleryCol);
          this._isGalleryInMobilePosition = true;
        }
      } else if (this._galleryAnchor && this._galleryAnchor.parentNode) {
        this._galleryAnchor.parentNode.insertBefore(this.galleryCol, this._galleryAnchor.nextSibling);
        this._isGalleryInMobilePosition = false;
      }

      this.reinitMainSwiper();
    }

    reinitMainSwiper() {
      if (this.mainSwiper && typeof this.mainSwiper.destroy === 'function') {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }

      this.mainSlider = this.querySelector('.main-product-v2__gallery');
      this.initSliders();

      if (this.selectedVariant) {
        this.updateImageVisibility(this.selectedVariant.id);
      } else {
        this.updateImageVisibility(null);
      }
    }

    onChangeSellingPlans() {
      if (!this.sellingPlanInputs) return;
      this.sellingPlanInputs.forEach((input) => {
        input.addEventListener('change', () => {
          const planId = input.value;

          this.setSellingPlanForATCFormInput(planId);
          this.updateSellingPlanParam(planId);

          this.toggleSubscriptionState(planId);
          this.updateATCButtonPrice(planId);

          // Update variant ID based on selling plan selection
          if (this.selectedVariant) {
            this.setVariantForATCFormInput(this.selectedVariant.id);
          }
          this.updateAddToCartAvailability();
        });
      });
    }

    setSellingPlanForATCFormInput(planId) {
      const input = this.querySelector('.main-product-v2__selling-plan-id');
      if (input) input.value = planId || "";
    }

    updateSellingPlanParam(planId) {
      const url = new URL(window.location);

      if (planId) {
        url.searchParams.set("selling_plan", planId);
      } else {
        url.searchParams.delete("selling_plan");
      }

      window.history.replaceState(null, "", url);
    }

    initSellingPlanForVariant(variantId) {
      if (!variantId) return;

      const wrapper = this.querySelector(`.main-product-v2__selling-plans[data-variant-id="${variantId}"]`);
      let planId = "";

      if (wrapper) {
        const checkedInput = wrapper.querySelector('.main-product-v2__selling-plan-input:checked') ||
          wrapper.querySelector('.main-product-v2__selling-plan-input');
        planId = checkedInput ? checkedInput.value : "";
      }

      this.setSellingPlanForATCFormInput(planId);
      this.updateSellingPlanParam(planId);

      this.toggleSubscriptionState(planId);
      this.updateATCButtonPrice(planId);

      return planId;
    }

    onChangeVariant() {
      if (!this.optionInputs) return;
      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => {
          const selectedOptions = Array.from(this.optionInputs).filter((i) => i.checked).map((i) => i.value);

          const matchingVariant = this.product.variants.find((variant) => {
            return variant.options.every((opt) => selectedOptions.includes(opt));
          });

          if (matchingVariant) {
            this.updateSelectedVariantById(matchingVariant.id);
            this.onShowCurrentSellingPlan(matchingVariant.id);
            this.setVariantForATCFormInput(matchingVariant.id);
            this.initSellingPlanForVariant(matchingVariant.id);
            this.updateAddToCartAvailability();
          } else {
            this.updateAddToCartAvailability(false);
          }
        });
      });
    }

    toggleSubscriptionState(planId) {
      const isSubscription = !!planId;

      const subscriptionTexts = this.querySelectorAll('.main-product-v2__subscription-text');
      const singleTexts = this.querySelectorAll('.main-product-v2__sing-product-text');

      if (isSubscription) {
        subscriptionTexts.forEach((el) => el.classList.remove('main-product-v2__subscription-text--hidden'));
        singleTexts.forEach((el) => el.classList.add('main-product-v2__sing-product-text--hidden'));
      } else {
        subscriptionTexts.forEach((el) => el.classList.add('main-product-v2__subscription-text--hidden'));
        singleTexts.forEach((el) => el.classList.remove('main-product-v2__sing-product-text--hidden'));
      }
    }

    initSliders() {
      if (this.mainSlider) {
        const galleryRoot =
          this.mainSlider.closest('.main-product-v3__gallery') || this.mainSlider.parentElement;
        const arrowsRow = galleryRoot?.querySelector('.main-product-v3__gallery-arrows');
        const paginationEl =
          arrowsRow?.querySelector('.main-product-v3__pagination') ||
          galleryRoot?.querySelector('.main-product-v3__pagination');
        const prevEl = arrowsRow?.querySelector('.main-product-v3__gallery-arrow--prev');
        const nextEl = arrowsRow?.querySelector('.main-product-v3__gallery-arrow--next');
        this.mainSwiper = new Swiper(this.mainSlider, {
          slidesPerView: 1,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          keyboard: {
            enabled: true,
            onlyInViewport: false,
            pageUpDown: false,
          },
          mousewheel: {
            enabled: true,
            forceToAxis: true,
            releaseOnEdges: true,
          },
          navigation:
            prevEl && nextEl
              ? {
                  prevEl,
                  nextEl,
                }
              : undefined,
          pagination: paginationEl
            ? { el: paginationEl, clickable: true }
            : undefined,
        });
      }
    }

    initSelectedVariant() {
      const popularVariant = this.product.variants.find((v) => v.available && v.metafields.custom.is_most_popular);
      const variantId = this.getVariantIdFromUrl() ||
        popularVariant?.id ||
        this.getFirstAvailableVariantId();

      return this.getVariantById(variantId);
    }

    getVariantIdFromUrl() {
      return new URL(document.location).searchParams.get("variant");
    }

    getFirstAvailableVariantId() {
      const firstAvailableVariant = this.product.variants.find((variant) => variant.available);
      return firstAvailableVariant ? firstAvailableVariant.id : this.product.variants[0]?.id;
    }

    updateSelectedVariantById(variantId) {
      if (!variantId) return;
      this.updateVariantParam(variantId);
      this.selectedVariant = this.getVariantById(variantId);
      this.updateContainerVariantAttribute(variantId);
    }

    updateContainerVariantAttribute(variantId) {
      const root = this.querySelector('.main-product-v3__variant-root');
      if (root && variantId) root.setAttribute('data-variant', variantId);
      this.updateImageVisibility(variantId);
    }

    updateImageVisibility(variantId) {
      const useAltFilter = this.hasAttribute('data-upd-view');
      const variant =
        variantId != null && variantId !== ''
          ? this.getVariantById(variantId)
          : this.selectedVariant;

      const passesAltFilter = (img, baseVisible) => {
        if (!useAltFilter || !img.hasAttribute('data-upd-filter-idx')) return baseVisible;
        if (!variant) return false;
        const idx = parseInt(img.getAttribute('data-upd-filter-idx'), 10);
        if (Number.isNaN(idx) || idx < 0 || idx >= variant.options.length) return false;
        const altTrim = (img.getAttribute('alt') || '').trim();
        return baseVisible && variant.options[idx] === altTrim;
      };

      const variantIdsMatch = (img) => {
        if (!variantId) return true;
        const raw = img.getAttribute('data-variant');
        if (!raw) return true;
        return raw.split(',').map((v) => v.trim()).includes(String(variantId));
      };

      const setImgVisible = (img, show) => {
        img.style.display = show ? 'block' : 'none';
        const picture = img.closest('picture');
        if (picture) picture.style.display = show ? 'block' : 'none';
      };

      const setSlideVisible = (slide, show) => {
        slide.style.opacity = show ? '1' : '0';
        slide.style.pointerEvents = show ? 'auto' : 'none';
      };

      // Grouped slides: hide whole slide if its alt-group doesn't match selected
      // variant's option value. Within a visible slide, pick exactly ONE image:
      // prefer variant-specific match (data-variant includes selected id), fall
      // back to a generic image (no data-variant) so the slide isn't empty.
      const updateGrouped = (slideSelector) => {
        this.querySelectorAll(slideSelector).forEach((slide) => {
          const altGroup = slide.getAttribute('data-alt-group') || '';
          const slideAltMatches = !useAltFilter || !altGroup || !variant
            ? true
            : variant.options.some((opt) => (opt || '').trim() === altGroup.trim());

          setSlideVisible(slide, slideAltMatches);

          const imgs = Array.from(slide.querySelectorAll('img:not(.main-product-v2__sticker)'));
          if (!imgs.length) return;

          if (!slideAltMatches) {
            imgs.forEach((img) => setImgVisible(img, false));
            return;
          }

          imgs.forEach((img) => setImgVisible(img, false));
          const candidates = imgs.filter((img) => passesAltFilter(img, true));
          if (!candidates.length) return;

          const variantSpecific = candidates.filter(
            (img) => img.hasAttribute('data-variant') && variantIdsMatch(img),
          );
          const generic = candidates.filter((img) => !img.hasAttribute('data-variant'));
          const chosen = variantSpecific[0] || generic[0] || null;
          if (chosen) setImgVisible(chosen, true);
        });
      };

      updateGrouped('.main-product-v2__gallery-slide[data-upd-alt-slide-group]');
      updateGrouped('.main-product-v2__thumbnails-slide[data-upd-alt-slide-group]');

      // Ungrouped slides: visible unless their data-variant excludes selection.
      const ungroupedSelectors = [
        '.main-product-v2__gallery-slide:not([data-upd-alt-slide-group])',
        '.main-product-v2__thumbnails-slide:not([data-upd-alt-slide-group])',
      ];
      ungroupedSelectors.forEach((sel) => {
        this.querySelectorAll(sel).forEach((slide) => {
          const slideVariants = slide.getAttribute('data-variant');
          if (!slideVariants || !variantId) {
            setSlideVisible(slide, true);
            return;
          }
          const arr = slideVariants.split(',').map((v) => v.trim());
          setSlideVisible(slide, arr.includes(String(variantId)));
        });
      });

      if (this.mainSwiper) {
        const slidesArr = Array.from(
          this.querySelectorAll('.main-product-v2__gallery-slide'),
        );
        const firstVisibleIndex = slidesArr.findIndex((s) => s.style.opacity !== '0');
        if (firstVisibleIndex >= 0) this.mainSwiper.slideTo(firstVisibleIndex, 0);
      }
    }

    getVariantById(variantId) {
      return (
        this.product.variants.find((variant) => variant.id === +variantId) ||
        this.product.variants.find((variant) => variant.available) ||
        this.product.variants[0]
      );
    }

    updateVariantParam(variantId) {
      const newUrl = new URL(document.location);
      newUrl.searchParams.set("variant", variantId);
      window.history.replaceState(null, "", newUrl);
    }

    setVariantForATCFormInput(varinatId) {
      // Check if "Buy it Once" is selected (no selling plan)
      const sellingPlanInput = this.querySelector('.main-product-v2__selling-plan-id');
      const planId = sellingPlanInput?.value || '';

      // Only use onetime variant if "Buy it Once" is selected (planId is empty)
      if (!planId) {
        const fieldset = this.querySelector(`.main-product-v2__selling-plans[data-variant-id="${varinatId}"]`);
        const onetimeVariantId = fieldset?.getAttribute('data-otp-variant-id');

        // Use onetime variant ID if available, otherwise use the regular variant ID
        this.addToCartVarinatInput.value = onetimeVariantId || varinatId;
      } else {
        // Use regular variant ID when subscription is selected
        this.addToCartVarinatInput.value = varinatId;
      }
    }

    updateATCButtonPrice(planId) {
      const variant = this.selectedVariant;
      if (!variant) return;

      let price = variant.price;

      if (planId && variant.selling_plans) {
        const plan = variant.selling_plans.find((sp) => sp.id == planId);
        if (plan && plan.price != null) {
          price = plan.price;
        }
      }

      const priceEls = this.querySelectorAll('.main-product-v2__atc-button-price');
      priceEls.forEach((el) => {
        el.textContent = this.money(price);
      });

      if (this.priceValueEl) {
        this.priceValueEl.textContent = this.money(price);
      }

      if (this.stickyPriceEl) {
        this.stickyPriceEl.textContent = this.money(price);
      }
    }

    /**
     * Syncs disabled state for primary ATC buttons and sticky ATC from the
     * variant id currently in the cart form (handles OTP vs subscription IDs).
     * @param {boolean|undefined} forceAvailable — pass false when option combo has no variant
     */
    updateAddToCartAvailability(forceAvailable) {
      let available;

      if (forceAvailable === false) {
        available = false;
      } else if (forceAvailable === true) {
        available = true;
      } else if (this.product && this.product.available === false) {
        available = false;
      } else {
        const idInput = this.addToCartVarinatInput;
        const vid = idInput?.value ? +idInput.value : null;
        const v = vid ? this.getVariantById(vid) : this.selectedVariant;
        available = !!(v && v.available);
      }

      const purchaseButtons = this.querySelectorAll(
        '.main-product-v3__purchase .main-product-v2__atc-button',
      );

      const soldOutLabel =
        window.languages?.productFormSoldOut || 'Sold out';

      purchaseButtons.forEach((btn) => {
        btn.disabled = !available;
        btn.setAttribute('aria-disabled', available ? 'false' : 'true');

        if (!btn.hasAttribute('data-atc-available-text')) return;
        const textEl = btn.querySelector('.main-product-v3__atc-button-text');
        if (!textEl) return;
        const availableText = btn.getAttribute('data-atc-available-text') || '';
        textEl.textContent = available ? availableText : soldOutLabel;
      });

      const stickyBtn = this.querySelector('.main-product-v3__sticky-atc-btn');
      if (stickyBtn) {
        stickyBtn.disabled = !available;
        stickyBtn.setAttribute('aria-disabled', available ? 'false' : 'true');

        if (stickyBtn.hasAttribute('data-sticky-atc-available-text')) {
          const stickyAvailableText =
            stickyBtn.getAttribute('data-sticky-atc-available-text') || '';
          stickyBtn.textContent = available ? stickyAvailableText : soldOutLabel;
        }
      }

      this.addToCartButtons = this.querySelectorAll('.main-product-v2__atc-button');
    }

    onShowCurrentSellingPlan(variantId) {
      this.sellingPlans.forEach((sellingPlanWrapper) => {
        const sellingPlanWrapperId = +sellingPlanWrapper.getAttribute('data-variant-id');
        if (sellingPlanWrapperId == variantId) {
          sellingPlanWrapper.classList.remove('main-product-v2__selling-plans--hidden');
        } else {
          sellingPlanWrapper.classList.add('main-product-v2__selling-plans--hidden');
        }
      });
    }

    handleAddToCartButtonClick() {
      if (!this.addToCartForm || !this.addToCartButtons.length) return;

      this.addToCartForm.addEventListener('click', (e) => {
        const clickedButton = e.target.closest('.main-product-v2__atc-button');
        if (!clickedButton) return;

        this._isRedirecting = false;

        const form = this.addToCartForm;
        const formData = new FormData(form);
        const mainVariantSellingPlan = formData.get("selling_plan") ? +formData.get("selling_plan") : null;

        let useAlternativeSubLink = false;
        if (window.useAltCheckoutLink) useAlternativeSubLink = true;

        let checkoutLink = null;
        if (mainVariantSellingPlan) {
          checkoutLink = useAlternativeSubLink
            ? this.selectedVariant?.metafields?.custom?.checkout_sub_link_alternative
            : this.selectedVariant?.metafields?.custom?.checkout_link;
        } else {
          checkoutLink = this.selectedVariant?.metafields?.custom?.checkout_otp_link;
        }

        if (checkoutLink) {
          checkoutLink = this.removeCartClearFromCheckoutLink(checkoutLink);
          e.preventDefault();
          e.stopPropagation();

          this._isRedirecting = true;
          window.location.replace(checkoutLink);
        }
      });
    }

    async addToCartAndCheckout(form) {
      if (!form) return;

      form.addEventListener('submit', async (e) => {
        if (this._isRedirecting) {
          e.preventDefault();
          return;
        }

        e.preventDefault();

        this.addToCartButtons.forEach((btn) => { btn.disabled = true; });

        try {
          const data = new FormData(form);
          const mainVariantSellingPlan = data.get("selling_plan") ? +data.get("selling_plan") : null;
          const mainVariantId = +data.get("id");

          const itemsToAdd = [{
            id: mainVariantId,
            quantity: 1,
            ...(mainVariantSellingPlan ? { selling_plan: mainVariantSellingPlan } : {})
          }];

          (window.UPSELL_PRODUCTS || []).forEach((upsellId) => {
            itemsToAdd.push({
              id: +upsellId,
              quantity: 1
            });
          });

          if (
            mainVariantSellingPlan &&
            window.UPSELL_FOR_SUBSCRIPTION &&
            window.UPSELL_FOR_SUBSCRIPTION[mainVariantId]
          ) {
            window.UPSELL_FOR_SUBSCRIPTION[mainVariantId].forEach((subsId) => {
              itemsToAdd.push({
                id: +subsId,
                quantity: 1
              });
            });
          }

          await this.addItemsToCart(itemsToAdd);
        } catch (err) {
          console.error("PDP add error:", err);
        } finally {
          this.updateAddToCartAvailability();
        }
      });
    }

    async addItemsToCart(items) {
      if (!items.length) return;

      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.description || res.statusText);
      }

      return await res.json();
    }

    removeCartClearFromCheckoutLink(url) {
      if (!url || typeof url !== 'string') return url;
      try {
        const parsed = new URL(url);
        if (parsed.pathname !== '/cart/clear') return url;

        const returnTo = parsed.searchParams.get('return_to');
        if (!returnTo) return url;

        const decoded = decodeURIComponent(returnTo);
        if (!decoded.startsWith('/')) return url;
        return parsed.origin + decoded;
      } catch {
        return url;
      }
    }

    money(price) {
      const formatted = this.moneyFormatter.format(price / 100.0);
      // Remove "A" prefix from AUD currency (A$ -> $)
      return formatted.replace(/^A\$/, '$');
    }
  }

  customElements.define("main-product-v3", mainProductV3);
}

